import { ReturnedReason } from "@/components/ReturnedReason";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatGBP, formatDateUK } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import DocumentPreview from "@/components/DocumentPreview";
import AuditTimeline from "@/components/AuditTimeline";
import ReviewPanel from "@/components/ReviewPanel";
import RecordFlags from "@/components/RecordFlags";
import {
  ArrowLeft, FileText, Loader2, Receipt, CreditCard, CheckCircle2, Clock, XCircle,
} from "lucide-react";


const STATUS_INFO = {
  draft: { icon: Clock, color: "text-gray-500", bg: "bg-gray-50", label: "Draft" },
  submitted: { icon: Clock, color: "text-blue-600", bg: "bg-blue-50", label: "Submitted for Approval" },
  approved: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", label: "Approved" },
  rejected: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", label: "Returned for correction" },
  voided: { icon: XCircle, color: "text-gray-600", bg: "bg-gray-100", label: "Voided" },
};

export default function ViewExpensePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    api.get(`/expenses/${id}`).then((r) => { if (live) setData(r.data); }).catch(() => {
      if (!live) return;
      toast.error("Expense not found or not accessible", { id: "not-found" });
      navigate("/submissions");
    }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [id, navigate, reload]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;
  if (!data) return null;

  const status = STATUS_INFO[data.status] || STATUS_INFO.draft;
  const StatusIcon = status.icon;

  return (
    <div className="space-y-5" data-testid="view-expense-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/submissions")} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-navy/40 hover:text-navy" aria-label="Back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-heading font-bold text-navy">Expense Details</h1>
      </div>

      {/* Status */}
      <div className={`${status.bg} rounded-xl p-4 flex items-center gap-3`}>
        <StatusIcon className={`w-5 h-5 ${status.color}`} />
        <div>
          <p className={`font-semibold text-sm ${status.color}`}>{status.label}</p>
          {data.submitted_by_name && <p className="text-xs text-navy/50 mt-0.5">Submitted by {data.submitted_by_name}</p>}
          {data.status === "rejected" && <ReturnedReason history={data.history} testid="expense-returned-reason" />}
        </div>
        <p className="ml-auto font-mono text-lg font-bold text-navy">{formatGBP(data.amount)}</p>
      </div>

      {/* Details */}
      <Card className="border-navy/5 shadow-none">
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold text-navy text-sm flex items-center gap-2">
            <Receipt className="w-4 h-4 text-gold" /> Expense Information
          </h3>
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2.5 text-sm">
            <><span className="text-navy/50">Category</span><span className="text-navy font-medium">{data.category}</span></>
            {data.supplier_payee && (
              <><span className="text-navy/50">Payee</span><span className="text-navy">{data.supplier_payee}</span></>
            )}
            {data.date && (
              <><span className="text-navy/50">Date</span><span className="text-navy">{formatDateUK(data.date)}</span></>
            )}
            <><span className="text-navy/50">Amount</span><span className="font-mono font-bold text-navy">{formatGBP(data.amount)}</span></>
            <><span className="text-navy/50">VAT</span><span className="font-mono text-navy">{formatGBP(data.vat_amount || 0)}</span></>
            <><span className="text-navy/50">Payment method</span><span className="text-navy capitalize">{data.payment_method ? data.payment_method.replace("_", " ") : "Not recorded"}</span></>
            {data.store_name && (
              <><span className="text-navy/50">Store</span><span className="text-navy">{data.store_name}</span></>
            )}
            {data.manager_exception && (
              <><span className="text-navy/50">Proof</span><span className="text-amber-600 font-medium">Manager Exception</span></>
            )}
          </div>
        </CardContent>
      </Card>

      {data.description && (
        <Card className="border-navy/5 shadow-none">
          <CardContent className="p-4">
            <h3 className="font-semibold text-navy text-sm mb-2">Description</h3>
            <p className="text-sm text-navy/70">{data.description}</p>
          </CardContent>
        </Card>
      )}

      {data.notes && (
        <Card className="border-navy/5 shadow-none">
          <CardContent className="p-4">
            <h3 className="font-semibold text-navy text-sm mb-2">Notes</h3>
            <p className="text-sm text-navy/70">{data.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Documents */}
      {data.documents && data.documents.length > 0 && (
        <Card className="border-navy/5 shadow-none">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold text-navy text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-gold" /> Supporting Documents ({data.documents.length})
            </h3>
            {data.documents.map((doc) => (
              <DocumentPreview key={doc.id} doc={doc} />
            ))}
          </CardContent>
        </Card>
      )}

      <RecordFlags data={data} entity="expenses" />
      <ReviewPanel entity="expenses" data={data} onDone={() => setReload((n) => n + 1)} />
      <AuditTimeline history={data.history} />
      <div className="text-xs text-navy/30 space-y-1 pb-4">
        {data.created_at && <p>Created: {formatDateUK(data.created_at)}</p>}
        {data.updated_at && <p>Updated: {formatDateUK(data.updated_at)}</p>}
        {data.approved_by && <p data-testid="approved-by">{data.status === "rejected" ? "Reviewed" : "Approved"} by: {data.approved_by_name || "Manager"}</p>}
      </div>
    </div>
  );
}

